import * as Sentry from '@sentry/browser'
import { get, set } from 'lodash'
import {
  SET_CLIENT_FETCH_ID,
  FETCH_CLIENT_REQUEST,
  FETCH_CLIENT_SUCCESS,
  FETCH_CLIENT_FAILURE,
  FETCH_CLIENT_HOMES_REQUEST,
  FETCH_CLIENT_HOMES_SUCCESS,
  FETCH_CLIENT_HOMES_FAILURE,
  SAVE_CLIENT_ANSWER_REQUEST,
  SAVE_CLIENT_ANSWER_SUCCESS,
  SAVE_CLIENT_ANSWER_FAILURE,
  SAVE_CLIENT_DATA_REQUEST,
  SAVE_CLIENT_DATA_SUCCESS,
  SAVE_CLIENT_DATA_FAILURE
} from './actionTypes'

import { QUESTION_DATE } from 'src/constants/client/onboarding'
import { QUESTION_HOME_INSURANCE_MODULE } from 'src/constants/client/answers'
import { SERVER_DATE } from 'src/constants/formats'

import { getClientHomes as requestGetClientHomes } from 'src/api/node/requests'
import {
  getClientData,
  getClientAnswers,
  createAnswer,
  updateAnswer,
  enableHBB,
  setMarketInterest,
  patchClientAttributeFields
} from 'src/api/mikasa/requests'
import { selectHomeData, selectZipcode } from 'src/store/selectors/home'
import { selectClientId } from 'src/store/selectors/client'
import { setLocaleData } from 'src/actions/locale'
import { setProfile } from 'src/actions/customerProfile'
import { fetchBuyerInfoData } from 'src/actions/buyerInfo'
import { flashNotification } from 'src/actions/notification'

export const setCurrentClient = clientId => async dispatch => {
  dispatch({
    type: SET_CLIENT_FETCH_ID,
    id: clientId
  })
}

export const fetchClientData = clientId => async dispatch => {
  dispatch({
    type: FETCH_CLIENT_REQUEST
  })

  try {
    const [buyerResponse, answerResponse] = await Promise.all([
      getClientData(clientId),
      getClientAnswers(clientId)
    ])
    const data = Object.assign(
      {},
      get(buyerResponse, 'data.data'),
      set({}, 'clientAnswers', get(answerResponse, 'data.data'))
    )

    dispatch({
      type: FETCH_CLIENT_SUCCESS,
      data
    })

    dispatch(setLocaleData(data.locale))

    if (get(buyerResponse, 'data.data.customerProfile')) {
      dispatch(setProfile(buyerResponse.data.data.customerProfile))
    }
  } catch (error) {
    Sentry.captureException(error)
    dispatch({
      type: FETCH_CLIENT_FAILURE,
      error
    })
  }
}

export const fetchClientHomes = legacyClientId => async dispatch => {
  dispatch({
    type: FETCH_CLIENT_HOMES_REQUEST
  })

  try {
    const response = await requestGetClientHomes(legacyClientId)
    dispatch({
      type: FETCH_CLIENT_HOMES_SUCCESS,
      data: response.data
    })
  } catch (error) {
    dispatch({
      type: FETCH_CLIENT_HOMES_FAILURE,
      error
    })
  }
}

const tolerate422 = promise =>
  promise.catch(error =>
    Promise[get(error, 'response.status') === 422 ? 'resolve' : 'reject'](error)
  )

export const setupBuyer = affordability => async (dispatch, getState) => {
  const state = getState()
  const home = selectHomeData(state)
  const clientId = selectClientId(state)
  const zipcode = selectZipcode(state)

  if (!home || !clientId) {
    return
  }

  const enableRequest = enableHBB(clientId, { affordability })

  const marketIntRequest = setMarketInterest(clientId, zipcode)

  try {
    await Promise.all([enableRequest, tolerate422(marketIntRequest)])
    dispatch({
      type: SET_CLIENT_FETCH_ID,
      id: clientId
    })
    dispatch(fetchClientData(clientId))
    dispatch(fetchBuyerInfoData(clientId))
  } catch (error) {
    Sentry.captureException(error)
  }
}

export const saveClientAnswer = (answer, notify) => async (
  dispatch,
  getState
) => {
  const client = get(getState(), 'client.data')

  if (!client) {
    return
  }

  dispatch({
    type: SAVE_CLIENT_ANSWER_REQUEST
  })

  const existingAnswer =
    client.clientAnswers &&
    client.clientAnswers.find(({ question: q }) => q === answer.question)

  const reqMethod = existingAnswer ? updateAnswer : createAnswer
  try {
    const { data } = await reqMethod(client.id, {
      ...existingAnswer,
      ...answer
    })
    dispatch({
      type: SAVE_CLIENT_ANSWER_SUCCESS,
      data: data.data
    })
    if (notify && notify.successMessage) {
      dispatch(
        flashNotification({
          message: notify.successMessage,
          messageType: 'success'
        })
      )
    }
  } catch (e) {
    dispatch({
      type: SAVE_CLIENT_ANSWER_FAILURE,
      error: e
    })
    if (notify && notify.failureMessage) {
      dispatch(
        flashNotification({
          message: notify.failureMessage
        })
      )
    }
  }
}

export const saveCloseDate = mom =>
  saveClientAnswer({
    question: QUESTION_DATE,
    response: {
      value: mom.format(SERVER_DATE)
    }
  })

export const saveHomeInsuranceOptOut = value =>
  saveClientAnswer({
    question: QUESTION_HOME_INSURANCE_MODULE,
    response: { value }
  })

export const saveClientData = attributes => async (dispatch, getState) => {
  const state = getState()
  const clientId = selectClientId(state)

  dispatch({
    type: SAVE_CLIENT_DATA_REQUEST,
    saveRequest: {
      clientId,
      attributes
    }
  })

  try {
    const response = await patchClientAttributeFields(clientId, attributes)
    dispatch({
      type: SAVE_CLIENT_DATA_SUCCESS,
      data: response.data
    })
  } catch (error) {
    dispatch({
      type: SAVE_CLIENT_DATA_FAILURE,
      error
    })
  }
}
